/**
 * Preferences › Voice › Shortcut and prompts › one prompt: Default or a
 * numbered prompt. Saved as you type. The chips add or remove their sentence
 * in the instructions, which stay free text; a numbered prompt also has a
 * name and can be deleted. While Default is switched off its text is kept
 * but inactive.
 */
import { useId, type ReactNode } from "react";
import {
  AutoResizeTextarea,
  Button,
  PocketRow,
  PocketSection,
} from "@/components/ui";
import {
  PROMPT_NAME_MAX,
  PROMPT_TEXT_MAX,
} from "@/desktop/dictation-anywhere/polish-prompts";
import { useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { cn } from "@/lib/cn";
import { useDictationAnywhereSettingsStore } from "@/stores/dictation-anywhere-settings-store";
import { useSettingsNav } from "./settings-navigation";
import { SettingsTextField } from "./settings-text-field";
import {
  DictationShortcutCaps,
  PromptKeyCaps,
} from "./voice-dictation-key-caps";
import {
  promptKeyPhrase,
  promptLabel,
  startStopPhrase,
  useDictationShortcutBinding,
} from "./voice-dictation-keys";
import { DEFAULT_PROMPT_ID } from "./voice-dictation-prompts-page";
import { VoicePromptChips } from "./voice-prompt-chips";

function Instructions({
  text,
  onChange,
  inactive = false,
}: {
  text: string;
  onChange: (text: string) => void;
  /** Default switched off: kept, but greyed out and not editable. */
  inactive?: boolean;
}) {
  const id = useId();
  const t = useT(settingsVoiceMessages);
  return (
    <fieldset
      disabled={inactive}
      className={cn("m-0 min-w-0 border-0 p-0", inactive && "opacity-50")}
    >
      <div className="px-2 py-1.5">
        <label
          htmlFor={id}
          className="mb-1 block text-appkit-small font-medium text-muted"
        >
          {t("instructions")}
        </label>
        <AutoResizeTextarea
          id={id}
          value={text}
          maxHeight={240}
          maxLength={PROMPT_TEXT_MAX}
          placeholder={t("instructionsPlaceholder")}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-[112px] rounded-[16px] px-[18px] py-[9px]"
          data-testid="voice-prompt-instruction"
        />
      </div>
      <VoicePromptChips text={text} onChange={onChange} />
    </fieldset>
  );
}

function ShortcutInfo({ caption, caps }: { caption: string; caps: ReactNode }) {
  const t = useT(settingsVoiceMessages);
  return (
    <PocketSection>
      <PocketRow
        testId="voice-prompt-shortcut"
        title={t("shortcut")}
        secondLine={caption}
        trailing={caps}
      />
    </PocketSection>
  );
}

function DefaultPromptEditor() {
  const t = useT(settingsVoiceMessages);
  useDictationShortcutBinding();
  const defaultPrompt = useDictationAnywhereSettingsStore(
    (state) => state.defaultPrompt,
  );
  const setInstruction = useDictationAnywhereSettingsStore(
    (state) => state.setDefaultInstruction,
  );
  return (
    <>
      <PocketSection>
        <Instructions
          text={defaultPrompt.instruction}
          onChange={setInstruction}
          inactive={!defaultPrompt.enabled}
        />
      </PocketSection>
      <ShortcutInfo
        caption={t("toStop", { start: startStopPhrase() })}
        caps={<DictationShortcutCaps />}
      />
    </>
  );
}

function NumberedPromptEditor({ id }: { id: string }) {
  const nav = useSettingsNav();
  const t = useT(settingsVoiceMessages);
  const prompts = useDictationAnywhereSettingsStore((state) => state.prompts);
  const updatePrompt = useDictationAnywhereSettingsStore(
    (state) => state.updatePrompt,
  );
  const deletePrompt = useDictationAnywhereSettingsStore(
    (state) => state.deletePrompt,
  );
  const index = prompts.findIndex((prompt) => prompt.id === id);
  const prompt = prompts[index];
  if (!prompt) {
    return (
      <p className="px-2 text-appkit-small text-muted">
        {t("promptUnavailable")}
      </p>
    );
  }
  const number = index + 1;
  return (
    <>
      <PocketSection>
        <SettingsTextField
          label={t("name")}
          value={prompt.name}
          placeholder={promptLabel({ name: "" }, index)}
          maxLength={PROMPT_NAME_MAX}
          onChange={(event) => updatePrompt(id, { name: event.target.value })}
          testId="voice-prompt-name"
        />
        <Instructions
          text={prompt.instruction}
          onChange={(instruction) => updatePrompt(id, { instruction })}
        />
      </PocketSection>
      <ShortcutInfo
        caption={t("whileListening", { press: promptKeyPhrase(number) })}
        caps={<PromptKeyCaps number={number} />}
      />
      <div className="mt-3 flex items-center px-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-danger hover:text-danger"
          onClick={() => {
            deletePrompt(id);
            nav.back();
          }}
          data-testid="voice-prompt-delete"
        >
          {t("deletePrompt")}
        </Button>
      </div>
    </>
  );
}

export function VoiceDictationPromptEditPage({
  promptId,
}: {
  /** `default`, or a numbered prompt's id. */
  promptId?: string;
}) {
  return (
    <div data-testid="settings-page-preferences-voice-prompt-edit">
      {promptId === DEFAULT_PROMPT_ID ? (
        <DefaultPromptEditor />
      ) : (
        <NumberedPromptEditor id={promptId ?? ""} />
      )}
    </div>
  );
}
